import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  cloneFactionProfile,
  deriveFactionProfiles,
  fetchArtCoverage,
  fetchFactionProfiles,
  fetchLoadoutSources,
  fetchLoadouts,
  importLoadouts,
  markFactionIrregular,
  setFactionRidesFrom,
  fetchReadiness,
  fetchScans,
  downloadTerrainMap,
  downloadTerrainMaps,
  fetchSummary,
  fetchSurveyStatus,
  pauseSurveys,
  fetchTerrainMaps,
  fetchTerrainProfiles,
  fetchTerrains,
  saveLoadout,
  scanTerrains,
  startScan,
  surveyAllTerrains,
  surveyTerrain,
  validateLoadout,
  type CatalogueSummary,
  type ArtCoverage,
  type FactionProfileView,
  type LoadoutCandidate,
  type LoadoutSources,
  type LoadoutValidation,
  type LoadoutView,
  type Readiness,
  type ScanView,
  type SurveyStatus,
  type TerrainMapState,
  type TerrainProfileView,
  type TerrainView,
} from '../api/catalog';
import { ApiError, OfflineError } from '../api/http';
import type { PlayableServer } from '../api/servers';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { VoiceRegionTable } from './VoiceRegions';
import { Compositions } from './Compositions';
import { ForcePresets } from './ForcePresets';
import { FactionRosterEditor } from './FactionRoster';
import { HelpMore } from './HelpMore';
import { ServerPicker } from './ServerPicker';
import { ModOriginChip, ModOriginChips } from './ModOrigin';
import { Shell } from './Shell';
import { TerrainImage } from './TerrainImage';
import { terrainOrigin } from './terrainOrigin';

function errorText(err: unknown): string {
  if (err instanceof OfflineError) return ca.errors.offline;
  return err instanceof ApiError ? (ca.errors[err.code] ?? ca.errors.unknown) : ca.errors.unknown;
}

/**
 * What a terrain's survey chip says. A profile that is DONE for a PBO the panel
 * no longer has is not "surveyed": the platform counts it among the pending,
 * and so does the chip.
 */
function surveyState(
  profile: TerrainProfileView | undefined,
  pending: boolean,
): 'QUEUED' | 'STARTING' | 'RUNNING' | 'DONE' | 'FAILED' | 'NONE' {
  if (profile && profile.status !== 'DONE') return profile.status;
  if (pending) return 'NONE';
  return profile ? 'DONE' : 'NONE';
}

function surveyChipClass(profile: TerrainProfileView | undefined, pending: boolean): string {
  const state = surveyState(profile, pending);
  if (state === 'DONE') return 'ok';
  if (state === 'FAILED') return 'bad';
  if (state === 'RUNNING' || state === 'STARTING' || state === 'QUEUED') return 'busy';
  return 'warn';
}

type Tab = 'terrains' | 'factions' | 'loadouts' | 'forces' | 'compositions';

function validationText(v: LoadoutValidation): string {
  return v.missing.length
    ? ca.loadoutMissing(v.missing.length)
    : v.catalogueKnown
      ? ca.loadoutOk
      : ca.notScanned;
}

/**
 * The catalogue page: a server (its mods are the mod set), the steps that make it ready, and
 * what it offers under three tabs (work packages 2.1 to 2.5).
 */
export function Catalog() {
  const { me } = useAuth();
  const [server, setServer] = useState<PlayableServer | null>(null);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [scan, setScan] = useState<ScanView | null>(null);
  const [summary, setSummary] = useState<CatalogueSummary | null>(null);
  const [terrains, setTerrains] = useState<TerrainView[]>([]);
  /** One survey per world, keyed by world class: the chip each terrain row shows. */
  const [profiles, setProfiles] = useState<Record<string, TerrainProfileView>>({});
  const [surveys, setSurveys] = useState<SurveyStatus | null>(null);
  /**
   * The terrains the last run refused to queue: modded worlds no scan knows, and
   * so worlds whose survey mission would have to guess the terrain addon.
   */
  const [surveySkipped, setSurveySkipped] = useState<string[]>([]);
  /** The terrain maps this host has downloaded from the Atlas, keyed by world class. */
  const [maps, setMaps] = useState<Record<string, TerrainMapState>>({});
  const [factions, setFactions] = useState<FactionProfileView[]>([]);
  /** The hand-made faction whose roster is open in the editor, by profile id. */
  const [rosterOpen, setRosterOpen] = useState<number | null>(null);
  const [loadouts, setLoadouts] = useState<LoadoutView[]>([]);
  const [sqf, setSqf] = useState('');
  const [loadoutName, setLoadoutName] = useState('');
  const [validation, setValidation] = useState<LoadoutValidation | null>(null);
  const [sources, setSources] = useState<LoadoutSources>({ missions: [], sessions: [] });
  const [importFrom, setImportFrom] = useState('');
  const [candidates, setCandidates] = useState<LoadoutCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // `?tab=` opens a tab: the loadout editor's link back lands on the loadouts.
  const [searchParams] = useSearchParams();
  const wantedTab = searchParams.get('tab');
  const [tab, setTab] = useState<Tab>(
    ['terrains', 'factions', 'loadouts', 'forces', 'compositions'].includes(wantedTab ?? '')
      ? (wantedTab as Tab)
      : 'terrains',
  );
  /**
   * Which factions would show their real flag. Asked for on demand rather than
   * on every render: it opens a PBO per faction the first time, and the answer
   * is then cached on the platform for as long as it runs.
   */
  const [coverage, setCoverage] = useState<ArtCoverage[] | null>(null);
  const [terrainQuery, setTerrainQuery] = useState('');
  /** Only the terrains whose survey is not done yet. */
  const [onlyMissing, setOnlyMissing] = useState(false);
  /** The class column is an identifier nobody reads; it is off until somebody asks. */
  const [showClass, setShowClass] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const canScan = me.roles.includes('SERVER_MANAGER') || me.roles.includes('ADMIN');
  // Marking a faction irregular is an editor's call, as the rest of a profile
  // (release D, D5.5; the design's recommendation, owner question 3).
  const canMark = me.roles.includes('EDITOR') || me.roles.includes('ADMIN');
  const c = ca.catalogPage2;

  /** An editor's irregular mark, or the answer given back to the catalogue's names. */
  async function onMarkIrregular(id: number, value: string) {
    const irregular = value === 'yes' ? true : value === 'no' ? false : null;
    try {
      const saved = await markFactionIrregular(id, irregular);
      setFactions((list) => list.map((f) => (f.id === id ? { ...f, ...saved } : f)));
    } catch (e) {
      setError(errorText(e));
    }
  }

  /** An editor's ride donor for a faction that owns no car, or none ('' clears it). */
  async function onRidesFrom(id: number, value: string) {
    try {
      const saved = await setFactionRidesFrom(id, value === '' ? null : value);
      setFactions((list) => list.map((f) => (f.id === id ? { ...f, ...saved } : f)));
    } catch (e) {
      setError(errorText(e));
    }
  }

  /**
   * A derived faction made into a hand-made one (docs/design/HANDMADE_ENEMY_FACTIONS.md):
   * a copy under its own name, whose roster opens in the editor at once.
   */
  async function onCloneForRoster(f: FactionProfileView) {
    if (!readiness?.modSetHash) return;
    try {
      const copy = await cloneFactionProfile(f.id, ca.factionRoster.cloneName(f.name));
      setFactions(await fetchFactionProfiles(readiness.modSetHash));
      setRosterOpen(copy.id);
    } catch (e) {
      setError(errorText(e));
    }
  }

  /** What a faction's men ride, as the list says it: its own, its groups', a donor's, or nothing. */
  function ridesText(f: FactionProfileView): string {
    switch (f.rides) {
      case 'own':
        return c.factionRidesOwn;
      case 'groups':
        return c.factionRidesGroups(f.ridesFaction ?? '');
      case 'donor':
        return c.factionRidesDonor(f.ridesFaction ?? '');
      case 'none':
        return c.factionRidesNone;
      default:
        return c.factionRidesUnknown;
    }
  }

  useEffect(() => {
    fetchLoadoutSources()
      .then(setSources)
      .catch(() => undefined);
  }, []);

  /**
   * Everything the page needs for a server's mod set. The calls used to run one after the
   * other — readiness, terrains, scans, then the summary, the factions and the
   * loadouts — so the page stayed empty for the sum of six round trips, three of
   * which go through to the panel. Only the readiness has to come first (it
   * carries the mod-set hash the rest are keyed by); the others go together.
   */
  /** The survey of every terrain, and how much of the mod set is still unread. */
  const loadSurveys = useCallback(async (serverId: number | null) => {
    const [list, status, mapList] = await Promise.all([
      fetchTerrainProfiles().catch(() => [] as TerrainProfileView[]),
      fetchSurveyStatus(serverId ?? undefined).catch(() => null),
      fetchTerrainMaps(serverId ?? undefined).catch(() => [] as TerrainMapState[]),
    ]);
    setProfiles(Object.fromEntries(list.map((profile) => [profile.worldName, profile])));
    setSurveys(status);
    setMaps(Object.fromEntries(mapList.map((m) => [m.worldName, m])));
  }, []);

  const loadForServer = useCallback(
    async (p: PlayableServer | null) => {
      setError(null);
      setSummary(null);
      setFactions([]);
      setScan(null);
      setLoading(true);
      try {
        if (!p) {
          setReadiness(null);
          setTerrains(await fetchTerrains().catch(() => []));
          await loadSurveys(null);
          return;
        }
        const r = await fetchReadiness(p.id);
        setReadiness(r);
        const [terrainList, scans] = await Promise.all([fetchTerrains(p.id), fetchScans()]);
        setTerrains(terrainList);
        await loadSurveys(p.id);
        setScan(scans.find((s) => s.modSetHash === r.modSetHash) ?? null);
        if (r.scanned) {
          const [s, f, l] = await Promise.all([
            fetchSummary(r.modSetHash),
            fetchFactionProfiles(r.modSetHash),
            fetchLoadouts(r.modSetHash),
          ]);
          setSummary(s);
          setFactions(f);
          setLoadouts(l);
        }
      } catch (err) {
        setError(errorText(err));
      } finally {
        setLoading(false);
      }
    },
    [loadSurveys],
  );

  useEffect(() => {
    if (me.authenticated) void loadForServer(null);
  }, [me.authenticated, loadForServer]);

  const scanning = scan?.status === 'RUNNING' || scan?.status === 'STARTING';
  /**
   * Scanned by a probe older than the mods table (v0.89.0): every mod of origin
   * reads "not read", and step 2 says so instead of a plain tick (card 224).
   */
  const originsStale = readiness?.scanned === true && readiness.modOrigins === false;
  const pendingWorlds = useMemo(
    () => new Set((surveys?.pending ?? []).map((t) => t.worldName)),
    [surveys],
  );
  const surveying = (surveys?.running ?? null) !== null || (surveys?.queued ?? 0) > 0;
  const mapsPending = Object.values(maps).filter((m) => m.available && !m.local);
  const mapsDownloading = Object.values(maps).some((m) => m.downloading);

  /**
   * A survey takes minutes and the queue takes as many of them as there are
   * terrains, so the page follows it the way it follows a scan: a poll while
   * something is running, and nothing at all when the ground is all read.
   */
  useEffect(() => {
    if (!surveying && !mapsDownloading) return;
    const timer = setInterval(() => {
      void loadSurveys(server?.id ?? null);
    }, 15000);
    return () => clearInterval(timer);
  }, [surveying, mapsDownloading, server, loadSurveys]);

  useEffect(() => {
    if (!scan || (scan.status !== 'RUNNING' && scan.status !== 'STARTING')) return;
    const timer = setInterval(() => {
      fetchScans()
        .then((scans) => {
          const current = scans.find((s) => s.id === scan.id) ?? null;
          setScan(current);
          if (current?.status === 'DONE') void loadForServer(server);
        })
        .catch(() => undefined);
    }, 10000);
    return () => clearInterval(timer);
  }, [scan, server, loadForServer]);

  async function run(key: string, work: () => Promise<void>) {
    setBusy(key);
    try {
      await work();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  /** One terrain, now: one server, a few minutes, and a profile at the end. */
  async function onSurvey(world: string, force = false) {
    await run(`survey:${world}`, async () => {
      await surveyTerrain(world, readiness?.modSetHash ?? null, force);
      await loadSurveys(server?.id ?? null);
    });
  }

  /**
   * Every terrain nobody has looked at, queued. With a server that is its mod
   * set's pending ones; without one it is every map the panel has, and then one
   * server at a time — that run owns the host for hours, and each terrain is
   * queued with the mod set a scan has already seen its world in.
   */
  async function onSurveyAll() {
    await run('survey-all', async () => {
      const queue = await surveyAllTerrains(server?.id ?? null, false, !server);
      setSurveySkipped(queue.skipped);
      await loadSurveys(server?.id ?? null);
    });
  }

  /** Pauses or resumes the survey queue; only an admin sees the button. */
  async function onPauseSurveys(paused: boolean) {
    await run('survey-pause', async () => {
      await pauseSurveys(paused);
      await loadSurveys(server?.id ?? null);
    });
  }

  /** One terrain's map onto this host: one request to the Atlas, then never again. */
  async function onDownloadMap(world: string) {
    await run(`map:${world}`, async () => {
      await downloadTerrainMap(world);
      await loadSurveys(server?.id ?? null);
    });
  }

  async function onDownloadMaps() {
    await run('maps', async () => {
      await downloadTerrainMaps(server?.id ?? null);
      await loadSurveys(server?.id ?? null);
    });
  }

  async function onScan(force = false) {
    if (!server) return;
    await run('scan', async () => setScan(await startScan(server.id, force)));
  }

  async function onDerive() {
    if (!readiness?.scanned) return;
    await run('derive', async () => {
      await deriveFactionProfiles(readiness.modSetHash);
      setFactions(await fetchFactionProfiles(readiness.modSetHash));
    });
  }

  async function onValidate() {
    await run('validate', async () =>
      setValidation(await validateLoadout(sqf, readiness?.modSetHash ?? null)),
    );
  }

  async function onSaveLoadout() {
    await run('save', async () => {
      const saved = await saveLoadout({
        name: loadoutName,
        sqf,
        modSetHash: readiness?.modSetHash ?? null,
      });
      setLoadouts((l) => [...l, saved]);
      setLoadoutName('');
      setSqf('');
      setValidation(null);
    });
  }

  async function onImport() {
    await run('import', async () => {
      const source = importFrom.startsWith('s:')
        ? { sessionCode: importFrom.slice(2) }
        : { mission: importFrom.slice(2) };
      setCandidates(await importLoadouts(source, readiness?.modSetHash ?? null));
    });
  }

  async function onSaveCandidate(cand: LoadoutCandidate) {
    await run('save', async () => {
      const saved = await saveLoadout({
        name: cand.name,
        sqf: cand.sqf,
        modSetHash: readiness?.modSetHash ?? null,
      });
      setLoadouts((l) => [...l, saved]);
      setCandidates((list) => (list ?? []).filter((x) => x !== cand));
    });
  }

  const shownTerrains = useMemo(() => {
    const q = terrainQuery.trim().toLowerCase();
    return terrains.filter(
      (t) =>
        (!q || t.worldName.toLowerCase().includes(q) || t.displayName.toLowerCase().includes(q)) &&
        (!onlyMissing ||
          surveyState(profiles[t.worldName], pendingWorlds.has(t.worldName)) !== 'DONE'),
    );
  }, [terrains, terrainQuery, onlyMissing, profiles, pendingWorlds]);

  const tabs: { key: Tab; label: string; icon: string; count: number | null }[] = [
    { key: 'terrains', label: c.tabTerrains, icon: 'bi-map', count: terrains.length },
    { key: 'factions', label: c.tabFactions, icon: 'bi-people', count: factions.length },
    { key: 'loadouts', label: c.tabLoadouts, icon: 'bi-backpack', count: loadouts.length },
    { key: 'forces', label: c.tabForces, icon: 'bi-diagram-3', count: null },
    { key: 'compositions', label: c.tabCompositions, icon: 'bi-bounding-box', count: null },
  ];

  const stepClass = (done: boolean, active: boolean) =>
    `step ${done ? 'done' : ''} ${active ? 'active' : ''}`;

  return (
    <Shell title={ca.catalog}>
      <HelpMore short={ca.catalogIntro} more={ca.catalogIntroMore} />
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {loading && (
        <p className="muted small" role="status">
          {ca.loading}
        </p>
      )}

      {/* The picker carries the one label ("Servidor (conjunt de mods)"); the form class styles its select. */}
      <section className="card catalog-head form">
        <ServerPicker
          value={server?.id ?? null}
          onChange={(p) => {
            setServer(p);
            void loadForServer(p);
          }}
        />
        {!server && <p className="muted small">{c.pickServerFirst}</p>}
        {server && readiness && (
          <ol className="readiness" aria-label={c.steps}>
            <li className={stepClass(true, false)}>
              <span className="mark" aria-hidden="true">
                <i className="bi bi-check-lg" />
              </span>
              <div className="body">
                <h3>{c.step1Title}</h3>
                <p className="muted small">{c.step1Intro}</p>
                <div className="row">
                  <span className="chip ok">
                    {ca.missionUi.details.presetMods(readiness.mods ?? 0)}
                  </span>
                  {(readiness.dlcs ?? []).map((d) => (
                    <span key={d} className="chip">
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            </li>
            <li className={stepClass(readiness.scanned, !readiness.scanned)}>
              <span className="mark" aria-hidden="true">
                {readiness.scanned ? <i className="bi bi-check-lg" /> : '2'}
              </span>
              <div className="body">
                <h3>{c.step2Title}</h3>
                <p className="muted small">{c.step2Intro}</p>
                <div className="row">
                  {scanning && scan ? (
                    <span className="chip warn" role="status">
                      {ca.scanning(scan.chunksReceived, scan.chunksExpected)}
                    </span>
                  ) : readiness.scanned && originsStale ? (
                    <span className="chip warn" title={c.scannedWithoutOriginsTitle}>
                      {c.scannedWithoutOrigins}
                    </span>
                  ) : readiness.scanned ? (
                    <span className="chip ok">{ca.scanned}</span>
                  ) : (
                    <span className="chip">{ca.notScanned}</span>
                  )}
                  {scan?.status === 'FAILED' && (
                    <span className="chip bad" title={scan.error ?? undefined}>
                      {ca.scanFailed}
                    </span>
                  )}
                  {canScan && (
                    <button
                      type="button"
                      className={`small ${readiness.scanned && !originsStale ? '' : 'primary'}`}
                      disabled={scanning || busy === 'scan'}
                      onClick={() => void onScan(readiness.scanned)}
                    >
                      <i className="bi bi-radar" aria-hidden="true" />
                      {readiness.scanned ? c.rescan : ca.scanContent}
                    </button>
                  )}
                </div>
                {scanning && scan && (
                  <progress
                    max={scan.chunksExpected ?? undefined}
                    value={scan.chunksExpected ? scan.chunksReceived : undefined}
                  />
                )}
              </div>
            </li>
            <li
              className={stepClass(factions.length > 0, readiness.scanned && factions.length === 0)}
            >
              <span className="mark" aria-hidden="true">
                {factions.length > 0 ? <i className="bi bi-check-lg" /> : '3'}
              </span>
              <div className="body">
                <h3>{c.step3Title}</h3>
                <p className="muted small">{c.step3Intro}</p>
                <div className="row">
                  <span className={`chip ${factions.length > 0 ? 'ok' : ''}`}>
                    {factions.length > 0 ? c.profilesCount(factions.length) : c.noProfiles}
                  </span>
                  {readiness.scanned && (
                    <button
                      type="button"
                      className="small"
                      disabled={busy === 'derive'}
                      onClick={() => void onDerive()}
                    >
                      <i className="bi bi-diagram-3" aria-hidden="true" />
                      {ca.deriveFactions}
                    </button>
                  )}
                </div>
              </div>
            </li>
          </ol>
        )}
        {summary && (
          <dl className="stats">
            <div className="stat">
              <dt>{c.statWorlds}</dt>
              <dd>{summary.worlds}</dd>
            </div>
            <div className="stat">
              <dt>{c.statFactions}</dt>
              <dd>{summary.factions}</dd>
            </div>
            <div className="stat">
              <dt>{c.statGroups}</dt>
              <dd>{summary.groups}</dd>
            </div>
            <div className="stat">
              <dt>{c.statVehicles}</dt>
              <dd>{summary.vehicles}</dd>
            </div>
            <div className="stat">
              <dt>{c.statWeapons}</dt>
              <dd>{summary.weapons}</dd>
            </div>
          </dl>
        )}
      </section>

      <div className="tabs" role="tablist">
        {tabs.map((tb) => (
          <button
            type="button"
            key={tb.key}
            role="tab"
            aria-selected={tab === tb.key}
            className={tab === tb.key ? 'active' : ''}
            onClick={() => setTab(tb.key)}
          >
            <i className={`bi ${tb.icon}`} aria-hidden="true" />
            {tb.label}
            {tb.count !== null && tb.count > 0 && <span className="chip">{tb.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'terrains' && (
        <section className="catalog-tab" aria-label={ca.terrains}>
          <p className="muted small">{ca.terrainsIntro}</p>
          <div className="toolbar">
            <input
              className="control"
              type="search"
              aria-label={c.terrainSearch}
              placeholder={c.terrainSearch}
              value={terrainQuery}
              onChange={(e) => setTerrainQuery(e.target.value)}
            />
            <button
              type="button"
              className={`chip toggle${onlyMissing ? ' on' : ''}`}
              aria-pressed={onlyMissing}
              onClick={() => setOnlyMissing((v) => !v)}
            >
              {c.terrainOnlyMissing}
            </button>
            <button
              type="button"
              className={`chip toggle${showClass ? ' on' : ''}`}
              aria-pressed={showClass}
              onClick={() => setShowClass((v) => !v)}
            >
              {c.terrainShowClass}
            </button>
            {canScan && (
              <button
                type="button"
                disabled={busy === 'terrains'}
                onClick={() =>
                  void run('terrains', async () => {
                    await scanTerrains();
                    await loadForServer(server);
                  })
                }
              >
                <i className="bi bi-map" aria-hidden="true" />
                {ca.scanTerrains}
              </button>
            )}
            {canScan && (surveys?.pending.length ?? 0) > 0 && (
              <button
                type="button"
                disabled={busy === 'survey-all' || surveying}
                onClick={() => void onSurveyAll()}
              >
                <i className="bi bi-binoculars" aria-hidden="true" />
                {server ? c.terrainSurveyAll : c.terrainSurveyAllMaps}
              </button>
            )}
          </div>
          {canScan && !server && (surveys?.pending.length ?? 0) > 0 && (
            <p className="muted small">{c.terrainSurveyAllMapsHint}</p>
          )}
          {surveySkipped.length > 0 && (
            <p className="notice warn">
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />{' '}
              {c.terrainSurveySkipped(surveySkipped.length)}{' '}
              <span className="muted">{surveySkipped.join(', ')}</span>
            </p>
          )}
          {/* Downloading a map is what stops the platform asking the Atlas for
              tiles at all: after this the ground is drawn from this host. */}
          {canScan && mapsPending.length > 0 && (
            <p className="muted small">
              {c.terrainMapHint}{' '}
              <button
                type="button"
                className="link"
                disabled={busy === 'maps' || mapsDownloading}
                onClick={() => void onDownloadMaps()}
              >
                {c.terrainMapAll} ({mapsPending.length})
              </button>
            </p>
          )}
          {/* The warning the whole feature exists for: a terrain nobody has
              surveyed is one the generator knows no water and no relief of, and
              a mod update brings new ones without saying so. */}
          {surveys && surveys.pending.length > 0 && (
            <p className="notice warn">
              <i className="bi bi-exclamation-triangle" aria-hidden="true" />{' '}
              {c.terrainSurveyPending(surveys.pending.length, surveys.total)}{' '}
              <span className="muted">{c.terrainSurveyHint}</span>
            </p>
          )}
          {surveys?.paused && (
            <p className="notice warn">
              <i className="bi bi-pause-circle" aria-hidden="true" /> {c.terrainSurveyPaused}
            </p>
          )}
          {me.roles.includes('ADMIN') &&
            (surveys?.paused || surveys?.running || (surveys?.queued ?? 0) > 0) && (
              <p>
                <button
                  type="button"
                  disabled={busy === 'survey-pause'}
                  onClick={() => void onPauseSurveys(!surveys?.paused)}
                >
                  <i
                    className={`bi ${surveys?.paused ? 'bi-play-circle' : 'bi-pause-circle'}`}
                    aria-hidden="true"
                  />
                  {surveys?.paused ? c.terrainSurveyResume : c.terrainSurveyPause}
                </button>
              </p>
            )}
          {surveys && (surveys.running || surveys.queued > 0) && (
            <p className="muted small">
              {surveys.running ? c.terrainSurveyRunning(surveys.running) : ''}
              {surveys.running && surveys.queued > 0 ? ' · ' : ''}
              {surveys.queued > 0 ? c.terrainSurveyQueued(surveys.queued) : ''}
              {surveys.sequential && surveys.queued > 0 ? ` · ${c.terrainSurveySequential}` : ''}
            </p>
          )}
          {shownTerrains.length === 0 ? (
            <div className="empty">
              <i className="bi bi-map" aria-hidden="true" />
              <p>{c.terrainsEmpty}</p>
              {terrains.length === 0 && <p className="small">{c.terrainsEmptyHint}</p>}
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th aria-hidden="true" />
                    <th>{c.terrainName}</th>
                    {showClass && <th>{c.terrainClass}</th>}
                    <th>{c.terrainSize}</th>
                    <th>{c.terrainSource}</th>
                    <th>{c.terrainMap}</th>
                    <th>{c.terrainSurvey}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownTerrains.map((t) => (
                    <tr key={t.id}>
                      <td className="thumb-cell">
                        <TerrainImage className="thumb mini" world={t.worldName} />
                      </td>
                      <td>
                        <Link
                          to={`/catalog/terrain/${encodeURIComponent(t.worldName)}${
                            readiness?.modSetHash ? `?set=${readiness.modSetHash}` : ''
                          }`}
                          aria-label={ca.terrainPage.open(t.displayName)}
                          title={t.worldName}
                        >
                          <strong>{t.displayName}</strong>
                        </Link>
                      </td>
                      {showClass && (
                        <td>
                          <code className="muted small">{t.worldName}</code>
                        </td>
                      )}
                      <td className="num">
                        {t.worldSize ? `${(t.worldSize / 1000).toFixed(1)} km` : '—'}
                      </td>
                      <td>
                        <span
                          className="chip"
                          title={t.modWorkshopId ? c.terrainModTitle(t.modWorkshopId) : undefined}
                        >
                          {t.modWorkshopId && <i className="bi bi-steam" aria-hidden="true" />}{' '}
                          {terrainOrigin(t)}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const map = maps[t.worldName];
                          if (!map || !map.available) {
                            return <span className="chip">{c.terrainMapNone}</span>;
                          }
                          if (map.downloading) {
                            return <span className="chip busy">{c.terrainMapDownloading}</span>;
                          }
                          if (map.local) {
                            return (
                              <span className="chip ok" title={c.terrainMapSize(map.bytes)}>
                                {c.terrainMapLocal}
                              </span>
                            );
                          }
                          return (
                            <>
                              <span className="chip">{c.terrainMapRemote}</span>
                              {canScan && (
                                <button
                                  type="button"
                                  className="link"
                                  disabled={busy === `map:${t.worldName}` || mapsDownloading}
                                  onClick={() => void onDownloadMap(t.worldName)}
                                >
                                  {c.terrainMapGet}
                                </button>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        <span
                          className={`chip ${surveyChipClass(profiles[t.worldName], pendingWorlds.has(t.worldName))}`}
                        >
                          {
                            c.terrainSurveyStates[
                              surveyState(profiles[t.worldName], pendingWorlds.has(t.worldName))
                            ]
                          }
                        </span>
                        {canScan &&
                          (pendingWorlds.has(t.worldName) ? (
                            // Not inspected yet: the action stays a visible button.
                            <button
                              type="button"
                              className="small"
                              disabled={busy === `survey:${t.worldName}` || surveying}
                              onClick={() => void onSurvey(t.worldName, false)}
                            >
                              {c.terrainSurveyDo}
                            </button>
                          ) : (
                            // Already inspected: a compact row action, named for whoever cannot see the icon.
                            <button
                              type="button"
                              className="small icon-only"
                              aria-label={`${c.terrainSurveyAgain}: ${t.displayName}`}
                              title={c.terrainSurveyAgain}
                              disabled={busy === `survey:${t.worldName}` || surveying}
                              onClick={() => void onSurvey(t.worldName, true)}
                            >
                              <i className="bi bi-arrow-repeat" aria-hidden="true" />
                            </button>
                          ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <VoiceRegionTable canEdit={me.roles.includes('ADMIN')} />
        </section>
      )}

      {tab === 'factions' && (
        <section className="catalog-tab" aria-label={ca.factions}>
          <div className="row between">
            <p className="muted small">{c.factionsIntro}</p>
            {readiness?.scanned && (
              <button type="button" disabled={busy === 'derive'} onClick={() => void onDerive()}>
                <i className="bi bi-diagram-3" aria-hidden="true" />
                {ca.deriveFactions}
              </button>
            )}
          </div>
          {factions.length > 0 && (
            <div className="card form" role="group" aria-label={c.flagsTitle}>
              <h3>{c.flagsTitle}</h3>
              <p className="muted small">{c.flagsHint}</p>
              <button
                type="button"
                disabled={!readiness?.modSetHash}
                onClick={() =>
                  void fetchArtCoverage(readiness!.modSetHash)
                    .then(setCoverage)
                    .catch((e) => setError(errorText(e)))
                }
              >
                {c.flagsCheck}
              </button>
              {coverage &&
                (coverage.every((f) => f.hasFlag) ? (
                  <p className="muted small">{c.flagsAllThere}</p>
                ) : (
                  <>
                    <p className="small">
                      {c.flagsMissing(coverage.filter((f) => !f.hasFlag).length)}
                    </p>
                    <ul className="list">
                      {coverage
                        .filter((f) => !f.hasFlag)
                        .map((f) => (
                          <li key={f.factionClass}>
                            <span className="grow">
                              <strong>{f.displayName}</strong>
                              <span className={`chip side-${f.side}`}>
                                {ca.sideNames[f.side] ?? f.side}
                              </span>
                            </span>
                            <code>{f.seedAs || '—'}</code>
                          </li>
                        ))}
                    </ul>
                  </>
                ))}
            </div>
          )}
          {factions.length === 0 ? (
            <div className="empty">
              <i className="bi bi-people" aria-hidden="true" />
              <p>{c.factionsEmpty}</p>
              <p className="small">{c.factionsEmptyHint}</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{c.factionName}</th>
                    <th>{c.factionSide}</th>
                    <th>{c.factionClass}</th>
                    <th>{ca.modOrigin.column}</th>
                    <th>{c.factionOrigin}</th>
                    <th>{c.factionIrregular}</th>
                    <th title={c.factionRidesHint}>{c.factionRides}</th>
                    <th title={ca.factionRoster.columnHint}>{ca.factionRoster.column}</th>
                  </tr>
                </thead>
                <tbody>
                  {factions.map((f) => (
                    <tr key={f.id}>
                      <td>
                        <strong>{f.name}</strong>
                      </td>
                      <td>
                        <span className={`chip side-${f.side}`}>
                          {ca.sideNames[f.side] ?? f.side}
                        </span>
                      </td>
                      <td>
                        <code className="muted small">{f.factionClass}</code>
                      </td>
                      <td>
                        <ModOriginChip origin={f.origin} />
                      </td>
                      <td>
                        <span className={`chip ${f.derived ? '' : 'warn'}`}>
                          {f.derived ? c.factionDerived : c.factionEdited}
                        </span>
                      </td>
                      <td>
                        {canMark && f.irregular !== undefined ? (
                          <select
                            name={`irregular-${f.id}`}
                            aria-label={c.factionIrregularOf(f.name)}
                            value={
                              f.irregularSource === 'hand'
                                ? f.irregular
                                  ? 'yes'
                                  : 'no'
                                : 'catalogue'
                            }
                            onChange={(e) => void onMarkIrregular(f.id, e.target.value)}
                          >
                            <option value="catalogue">
                              {c.factionIrregularByNames(
                                f.irregularSource === 'hand' ? null : !!f.irregular,
                              )}
                            </option>
                            <option value="yes">{c.factionIrregularYes}</option>
                            <option value="no">{c.factionIrregularNo}</option>
                          </select>
                        ) : f.irregular ? (
                          c.factionIrregularYes
                        ) : (
                          c.factionIrregularNo
                        )}
                      </td>
                      <td>
                        {f.rides === undefined ? (
                          '—'
                        ) : canMark && f.rides !== 'own' && f.rides !== 'groups' ? (
                          <>
                            <select
                              name={`rides-${f.id}`}
                              aria-label={c.factionRidesOf(f.name)}
                              value={f.ridesFrom ?? ''}
                              onChange={(e) => void onRidesFrom(f.id, e.target.value)}
                            >
                              <option value="">{c.factionRidesNoDonor}</option>
                              {[
                                ...new Map(
                                  factions
                                    .filter(
                                      (d) =>
                                        d.id !== f.id &&
                                        d.factionClass.toLowerCase() !==
                                          f.factionClass.toLowerCase(),
                                    )
                                    .map((d) => [d.factionClass.toLowerCase(), d]),
                                ).values(),
                              ]
                                .sort((a, b) => a.name.localeCompare(b.name, 'ca'))
                                .map((d) => (
                                  <option key={d.factionClass} value={d.factionClass}>
                                    {d.name} ({d.factionClass})
                                  </option>
                                ))}
                            </select>
                            <span className="muted small"> {ridesText(f)}</span>
                          </>
                        ) : (
                          ridesText(f)
                        )}
                      </td>
                      <td>
                        {f.roster ? (
                          <span className="small">
                            {ca.factionRoster.summary(
                              f.roster.men,
                              f.roster.vehicles,
                              f.roster.statics,
                            )}{' '}
                          </span>
                        ) : (
                          <span className="muted small">{ca.factionRoster.none} </span>
                        )}
                        {canMark &&
                          (f.derived ? (
                            <button
                              type="button"
                              className="small"
                              onClick={() => void onCloneForRoster(f)}
                            >
                              {ca.factionRoster.clone}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="small"
                              onClick={() => setRosterOpen(f.id)}
                            >
                              {f.roster ? ca.factionRoster.edit : ca.factionRoster.make}
                            </button>
                          ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rosterOpen !== null &&
            readiness?.modSetHash &&
            factions.some((f) => f.id === rosterOpen) && (
              <FactionRosterEditor
                key={rosterOpen}
                profile={factions.find((f) => f.id === rosterOpen)!}
                modSetHash={readiness.modSetHash}
                onSaved={() =>
                  void fetchFactionProfiles(readiness.modSetHash)
                    .then(setFactions)
                    .catch((e) => setError(errorText(e)))
                }
                onClose={() => setRosterOpen(null)}
              />
            )}
        </section>
      )}

      {tab === 'loadouts' && (
        <section className="catalog-tab" aria-label={ca.loadouts}>
          <div className="loadout-columns">
            <div className="card form">
              <h2>{c.loadoutPasteTitle}</h2>
              <p className="muted small">{c.loadoutPasteHelp}</p>
              <textarea
                name="sqf"
                rows={8}
                placeholder={ca.loadoutPaste}
                value={sqf}
                onChange={(e) => setSqf(e.target.value)}
              />
              {validation && (
                <p className={validation.missing.length ? 'error' : 'notice ok'} role="status">
                  {validation.missing.length
                    ? `${validationText(validation)}: ${validation.missing.join(', ')}`
                    : validationText(validation)}
                </p>
              )}
              <label>
                {ca.loadoutName}
                <input
                  name="loadoutName"
                  value={loadoutName}
                  onChange={(e) => setLoadoutName(e.target.value)}
                />
              </label>
              <div className="row actions">
                <button
                  type="button"
                  disabled={!sqf.trim() || busy === 'validate'}
                  onClick={() => void onValidate()}
                >
                  <i className="bi bi-check2-circle" aria-hidden="true" />
                  {ca.loadoutValidate}
                </button>
                <span className="grow" />
                <button
                  type="button"
                  className="primary"
                  disabled={!validation || !loadoutName.trim() || busy === 'save'}
                  onClick={() => void onSaveLoadout()}
                >
                  {ca.loadoutSave}
                </button>
              </div>
            </div>

            <div className="stack">
              <div className="card form">
                <h2>{c.loadoutImportTitle}</h2>
                <p className="muted small">{c.loadoutImportHelp}</p>
                <label>
                  {ca.loadoutImport}
                  <select value={importFrom} onChange={(e) => setImportFrom(e.target.value)}>
                    <option value="">{ca.loadoutImportNone}</option>
                    <optgroup label={ca.loadoutImportMission}>
                      {sources.missions.map((m) => (
                        <option key={`m:${m}`} value={`m:${m}`}>
                          {m}
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label={ca.loadoutImportSession}>
                      {sources.sessions.map((sess) => (
                        <option key={`s:${sess.code}`} value={`s:${sess.code}`}>
                          {sess.missionTitle} · {ca.loadoutImportPlayers(sess.players)}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </label>
                <div className="row actions">
                  <button
                    type="button"
                    disabled={!importFrom || busy === 'import'}
                    onClick={() => void onImport()}
                  >
                    <i className="bi bi-search" aria-hidden="true" />
                    {ca.loadoutImportGo}
                  </button>
                </div>
                {candidates && candidates.length === 0 && (
                  <p className="muted small">{ca.loadoutImportEmpty}</p>
                )}
                {candidates && candidates.length > 0 && (
                  <>
                    <h2 className="section">{c.loadoutCandidates}</h2>
                    <ul className="list" aria-label={c.loadoutCandidates}>
                      {candidates.map((cand) => (
                        <li key={cand.name}>
                          <div className="grow">
                            <strong>{cand.name}</strong>
                            <p
                              className={`small ${cand.validation.missing.length ? 'error' : 'muted'}`}
                            >
                              {validationText(cand.validation)}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="small"
                            onClick={() => {
                              setSqf(cand.sqf);
                              setLoadoutName(cand.name);
                              setValidation(cand.validation);
                            }}
                          >
                            {ca.loadoutImportEdit}
                          </button>
                          <button
                            type="button"
                            className="small primary"
                            onClick={() => void onSaveCandidate(cand)}
                          >
                            {ca.loadoutImportSave}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="card">
                <h2>
                  {c.loadoutSavedTitle}{' '}
                  {loadouts.length > 0 && <span className="chip">{loadouts.length}</span>}
                </h2>
                {readiness?.modSetHash && (
                  <p>
                    <Link
                      className="button small"
                      to={`/catalog/loadouts/new?hash=${readiness.modSetHash}`}
                    >
                      <i className="bi bi-plus-lg" aria-hidden="true" /> {ca.loadoutEditor.newKit}
                    </Link>
                  </p>
                )}
                {loadouts.length === 0 ? (
                  <p className="muted small">{c.loadoutSavedEmpty}</p>
                ) : (
                  <ul className="list" aria-label={c.loadoutSavedTitle}>
                    {loadouts.map((l) => (
                      <li key={l.id}>
                        <strong className="grow">{l.name}</strong>
                        <Link
                          className="button small"
                          to={`/catalog/loadouts/${l.id}${readiness?.modSetHash ? `?hash=${readiness.modSetHash}` : ''}`}
                          aria-label={ca.loadoutEditor.editKitFor(l.name)}
                        >
                          <i className="bi bi-pencil" aria-hidden="true" />{' '}
                          {ca.loadoutEditor.editKit}
                        </Link>
                        {l.role && <span className="chip">{l.role}</span>}
                        {l.side && (
                          <span className={`chip side-${l.side}`}>
                            {ca.sideNames[l.side] ?? l.side}
                          </span>
                        )}
                        <ModOriginChips origins={l.origins} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {tab === 'forces' && <ForcePresets modSetHash={readiness?.modSetHash ?? null} />}
      {tab === 'compositions' && (
        <Compositions
          modSetHash={readiness?.modSetHash ?? null}
          isAdmin={me.roles.includes('ADMIN')}
        />
      )}
    </Shell>
  );
}
